import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosRequestHeaders,
} from "axios";
import { queryClient } from "../queries/client";
import { getAuthStore, useAuthStore } from "../store/useAuthStore";
import { refetchAccessToken } from "../api/auth";
import { revalidateAuth } from "../utils/auth";

const api = axios.create({
  baseURL:
    process.env.REACT_APP_API || "https://dev-identity-api.hissa.com/v1/",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
});

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function onTokenRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

function addRefreshSubscriber(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 401 Unauthorized Error
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const newToken = await refetchAccessToken();
          getAuthStore().setAuth({ accessToken: newToken }); // Use Zustand store outside React
          onTokenRefreshed(newToken);
          isRefreshing = false;
          return api(originalRequest);
        } catch (refreshError) {
          isRefreshing = false;
          queryClient.clear();
          getAuthStore().clear();
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve) => {
        addRefreshSubscriber((token) => {
          if (originalRequest?.headers) {
            originalRequest.headers.accesstoken = `${token}`;
          }
          resolve(api(originalRequest));
        });
      });
    }

    return Promise.reject(error);
  }
);

api.interceptors.request.use((config) => {
  const accesstoken = getAuthStore().accessToken;
  revalidateAuth();
  if (accesstoken) {
    (config.headers as AxiosRequestHeaders).AccessToken = accesstoken;
  }
  return config;
});

export default api;
